/**
 * The panel's design tokens (A8 design pass, Figma "SlidePilot v2.0.0" admin frames). The
 * palettes are the add-in's (src/taskpane/core/shell/slideBrainTheme.ts), copied by value:
 * the web tree never imports plugin code, and the panel keeps its own copy so a change here
 * can't move the add-in (Dan 2026-10-02). Names follow the Figma variables (`brand/7`,
 * `gray/1`, `border/default`, `orange/8`, ...).
 */
import type { MantineColorsTuple } from "@mantine/core";

const blue: MantineColorsTuple = [
  "#ebf1ff", "#c9d9f4", "#a8c1e9", "#86a9df", "#6591d4",
  "#4378c9", "#2260bf", "#0048b4", "#003a90", "#002d70",
];
const gray: MantineColorsTuple = [
  "#f9fafb", "#eff1f3", "#d2dce6", "#bfc9d5", "#acb5c4",
  "#99a2b3", "#868ea2", "#737b91", "#4a5167", "#2e3340",
];
const green: MantineColorsTuple = [
  "#eefee8", "#c0e5c0", "#b0d6a7", "#91c186", "#72ad66",
  "#529945", "#338425", "#147004", "#105703", "#0c4102",
];
const red: MantineColorsTuple = [
  "#fff6f7", "#fbd4d4", "#efb2b1", "#e2918d", "#d66f6a",
  "#ca4d47", "#bd2c23", "#b10a00", "#8a0800", "#670600",
];
/** The add-in keys this palette `yellow`; the Figma file names it orange. */
const orange: MantineColorsTuple = [
  "#faf5df", "#f5ddbf", "#f0c59f", "#ecad7f", "#e79560",
  "#e27c40", "#de6420", "#d94c00", "#ae3d00", "#822e00",
];

export const PANEL_PALETTES = { blue, gray, green, red, orange };

/** Named colors, as the Figma variables call them. */
export const PANEL_COLOR = {
  brand: blue[7],
  /** `surface/background`: the page behind the cards. */
  surface: gray[0],
  /** `gray/1`: card header bands, table headers, progress tracks. */
  fill: gray[1],
  /** `border/default`. */
  border: gray[2],
  textPrimary: "#000000",
  /** `text/secondary`: meta lines ("Last saved", usage). */
  textSecondary: gray[7],
  /** `gray/8`: card and table header text. */
  headerText: gray[8],
  /** The modal backdrop's blue-tinted dim (the add-in's R25 vocabulary). */
  modalOverlay: "#0f1226",
} as const;

/** Spacing in px, from the frames. */
/** Every hairline in the panel: `border/default` through Mantine's default-border variable,
 * which the theme sets to PANEL_COLOR.border, so cards, inputs, tables and the shell share
 * one source. */
export const PANEL_BORDER = "1px solid var(--mantine-color-default-border)";

export const PANEL_SPACE = {
  /** Page padding and the gap between page blocks. */
  page: 20,
  /** A card's inner padding and the gap between its blocks. */
  card: 15,
  /** Between a label and its input, between stacked rows. */
  field: 10,
  /** Between two columns of fields. */
  columns: 30,
} as const;

export const PANEL_RADIUS = {
  /** Cards, tables, alerts. */
  card: 5,
  /** Inputs. */
  input: 3,
  /** Buttons (`radius/md`). */
  button: 8,
} as const;

export const PANEL_LAYOUT = {
  navWidth: 247,
  /** An inline field's label column (Brand Configuration). */
  inlineLabelWidth: 100,
  /** The page column's widest: wider screens center it, so lines stay readable. */
  contentMaxWidth: 1040,
  /** A full-width button in a form's button row (Cancel / Save). */
  formButtonWidth: 200,
} as const;

/** Body type is 13px Inter throughout (Dan 2026-10-02, one step under the frames' 14px); the
 * footer is 12px. The header wordmark is outlined SVG (shell/Wordmark.tsx), so no second font
 * loads. */
export const PANEL_FONT = {
  family: "'Inter Variable', system-ui, sans-serif",
  body: "13px",
  small: "12px",
} as const;
